// src/app/containers/Table.tsx

import React, {
  CSSProperties,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CellChange, CellLocation, ReactGrid, Row, TextCell } from '@silevis/reactgrid';
import { ProjectsContext } from 'src/app/providers/ProjectProvider';
import { TableContext } from 'src/app/providers/TableProvider';
import {
  ActionsCellTemplate,
  DateCellTemplate,
  DropdownCellTemplate,
  FileCellTemplate,
  HeaderCellTemplate,
  MyHeaderCell,
  NumberCellTemplate,
  TextCellTemplate,
} from 'src/entity/CellTemplates';
import { ProjectBusinessRulesModal } from 'src/feature/ProjectBusinessRulesModal';
import { ProjectColumnAccessModal } from 'src/feature/ProjectColumnAccessModal';
import { ProjectConsolidationModal } from 'src/feature/ProjectConsolidationModal';
import { ProjectImportDataModal } from 'src/feature/ProjectImportDataModal';
import { ProjectSettingsModal } from 'src/feature/ProjectSettingsModal';
import { ProjectTotalsRowModal } from 'src/feature/ProjectTotalsRowModal';
import { ProjectWorkflowModal } from 'src/feature/ProjectWorkflowModal';
import { TableCellMetaMode, TableCellMetaTarget } from 'src/feature/Table/hooks/useContextMenu';
import { BatchSubsetOperationModal } from 'src/feature/Table/ui/BatchSubsetOperationModal';
import { TableCellMetaModal } from 'src/feature/TableCellMetaModal';
import ProjectsService from 'src/shared/api/projects/ProjectsService';
import { TableSettingsForm } from 'src/shared/store/projects/types';
import { getProtectedBusinessRuleColumns } from 'src/shared/store/table/protectedColumns';
import { UserContext } from 'src/shared/store/user';
import { canWriteData } from 'src/shared/store/user/permissions';
import { CellTemplates as MyCellTemplates } from 'src/shared/types';
import { Loader } from 'src/shared/ui/Loader';

import {
  useCheckAddRow,
  useCheckPasteAddRow,
  useColumns,
  useContextMenu,
  useInfinityScroll,
} from '../hooks';
import { applyChanges, canHideIdColumn, getHeaderRow, getRows } from '../utils';
import {
  buildAppearanceCssVariables,
  buildScopedCustomCss,
  resolveTableAppearance,
} from '../utils/appearance';

import '@silevis/reactgrid/styles.css';
import '../styles/styles.css';

interface TableProps {
  tableSettings: TableSettingsForm;
  height: string;
  width: string;
  setFullWidth: Dispatch<SetStateAction<boolean>>;
}

export const Table: FC<TableProps> = ({ height, width, tableSettings, setFullWidth }) => {
  const {
    tableData,
    setTableData,
    setSort,
    isLoading,
    isEdit,
    setIsEdit,
    refreshTable,
    setSelectedRowId,
  } = useContext(TableContext);
  const { setTableSettings } = useContext(ProjectsContext);
  const { user } = useContext(UserContext);

  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenWorkflowModal, setIsOpenWorkflowModal] = useState(false);
  const [isOpenBusinessRulesModal, setIsOpenBusinessRulesModal] = useState(false);
  const [isOpenTotalsRowModal, setIsOpenTotalsRowModal] = useState(false);
  const [isOpenConsolidationModal, setIsOpenConsolidationModal] = useState(false);
  const [columnAccessColumnName, setColumnAccessColumnName] = useState<string | null>(null);
  const [isOpenImportModal, setIsOpenImportModal] = useState(false);
  const [isOpenSubsetModal, setIsOpenSubsetModal] = useState(false);
  const [cellMetaModal, setCellMetaModal] = useState<{
    isOpen: boolean;
    mode: TableCellMetaMode;
    target: TableCellMetaTarget | null;
  }>({
    isOpen: false,
    mode: 'comment',
    target: null,
  });
  const [isIdHidden, setIsIdHidden] = useState<boolean>(true);
  const canHideId = useMemo(
    () => canHideIdColumn(tableSettings?.formStructure || []),
    [tableSettings?.formStructure],
  );
  const effectiveIsIdHidden = canHideId ? isIdHidden : false;
  const memoTableSettings = useMemo(
    () =>
      !effectiveIsIdHidden
        ? tableSettings
        : {
            ...tableSettings,
            formStructure: tableSettings.formStructure.filter(
              ({ columnName }) => columnName.toLowerCase() !== 'id',
            ),
          },
    [effectiveIsIdHidden, tableSettings],
  );
  const appearance = useMemo(
    () => resolveTableAppearance(memoTableSettings?.appearanceSettings),
    [memoTableSettings?.appearanceSettings],
  );
  const [headerRows, setHeaderRows] = useState<Row<MyHeaderCell>>(
    getHeaderRow(memoTableSettings, appearance),
  );
  const appearanceScopeClass = useMemo(
    () => `table-appearance-${tableSettings.id}`,
    [tableSettings.id],
  );
  const customCss = useMemo(
    () =>
      buildScopedCustomCss(memoTableSettings?.appearanceSettings?.customCss, appearanceScopeClass),
    [appearanceScopeClass, memoTableSettings?.appearanceSettings?.customCss],
  );

  const reloadCurrentTableSettings = useCallback(async () => {
    try {
      const { data } = await ProjectsService.getTableSettings({ formId: tableSettings.id });
      if (!data) {
        return;
      }

      setTableSettings({
        id: data.Id || data['ID'],
        formDescription: data.FormDescription,
        alias: data.Alias,
        formName: data.Alias,
        connection: data.FormName,
        formStructure: data.FormStructure,
        isDirectory: data.IsDrectory,
        appearanceSettings: data.AppearanceSettings,
        viewSettings: data.ViewSettings,
        businessRules: data.BusinessRules,
        flkRules: data.FlkRules,
        workflowSettings: tableSettings.workflowSettings,
        directoryWorkflowSettings: tableSettings.directoryWorkflowSettings,
        workflowCurrentStatus: tableSettings.workflowCurrentStatus,
        workflowReadOnly: tableSettings.workflowReadOnly,
      });
    } catch (error) {
      console.error('Failed to refresh table settings after column access update', error);
    }
  }, [setTableSettings, tableSettings]);

  useEffect(() => {
    setHeaderRows(getHeaderRow(memoTableSettings, appearance));
  }, [memoTableSettings, appearance]);

  useEffect(() => {
    const contextMenu = document.querySelector('.rg-context-menu') as HTMLDivElement;

    if (contextMenu && contextMenu.style.visibility === 'visible') {
      contextMenu.style.visibility = 'hidden';
    }
  }, []);

  const canWrite = canWriteData(user) && !tableSettings?.workflowReadOnly;
  const protectedRuleColumns = useMemo(
    () => getProtectedBusinessRuleColumns(memoTableSettings?.businessRules),
    [memoTableSettings?.businessRules],
  );

  useCheckAddRow(setFullWidth, canWrite);
  useInfinityScroll();

  const { simpleHandleContextMenu } = useContextMenu({
    canHideIdColumn: canHideId,
    isIdHidden: effectiveIsIdHidden,
    setIsIdHidden,
    setIsOpenEditModal,
    setIsOpenWorkflowModal,
    setIsOpenBusinessRulesModal,
    setIsOpenTotalsRowModal,
    setIsOpenConsolidationModal,
    setIsOpenImportModal,
    setIsOpenSubsetModal,
    onOpenCellMetaModal: (target, mode) => {
      setCellMetaModal({
        isOpen: true,
        mode,
        target,
      });
    },
    onOpenColumnAccessModal: columnName => {
      setColumnAccessColumnName(columnName);
    },
  });
  const { setSelectedRowId: setPasteSelectedRowId } = useCheckPasteAddRow(canWrite);
  const { columns, handleColumnResize } = useColumns(memoTableSettings, width);
  const tableContainerStyle: CSSProperties = {
    height,
    width,
    ...buildAppearanceCssVariables(appearance),
  };

  useEffect(() => {
    if (!canWrite && isEdit) {
      setIsEdit(false);
    }
  }, [canWrite, isEdit, setIsEdit]);

  const handleChanges = (changes: CellChange<TextCell>[]) => {
    if (!canWrite) {
      return;
    }
    const updatedData = applyChanges(changes, tableData, setSort, setHeaderRows);
    // Здесь мы вызываем setTableData, что триггерит сохранение состояния в хук useUndoableState
    setTableData(updatedData);
  };

  const handleFocusLocationChanged = (location: CellLocation) => {
    const nextRowId =
      typeof location.rowId === 'number' && typeof tableData[location.rowId]?.id === 'number'
        ? (tableData[location.rowId]?.id as number)
        : null;
    setSelectedRowId(nextRowId);
    setPasteSelectedRowId(location.rowId);
  };

  const myCellTemplates: MyCellTemplates = {
    date: new DateCellTemplate(),
    header: new HeaderCellTemplate(),
    file: new FileCellTemplate(),
    actions: new ActionsCellTemplate(),
    floatNumber: new NumberCellTemplate(),
    dropdown: new DropdownCellTemplate(),
    number: new NumberCellTemplate(),
    text: new TextCellTemplate(),
  };

  return (
    <div
      id="table-container"
      className={`grid-table relative overflow-x-auto overflow-y-auto ${appearanceScopeClass}`}
      style={tableContainerStyle}
    >
      {customCss && <style>{customCss}</style>}
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-blue-rgba-100">
          <Loader classNames="w-32 h-32" />
        </div>
      )}

      <ReactGrid
        rows={getRows(
          tableData,
          headerRows,
          memoTableSettings?.formStructure || [],
          isEdit,
          memoTableSettings?.viewSettings,
          appearance,
          protectedRuleColumns,
        )}
        columns={columns}
        onColumnResized={handleColumnResize}
        onCellsChanged={handleChanges}
        onFocusLocationChanged={handleFocusLocationChanged}
        onContextMenu={simpleHandleContextMenu}
        customCellTemplates={myCellTemplates}
        stickyTopRows={1}
        enableFillHandle={canWrite}
        enableRangeSelection
      />

      {isOpenEditModal && <ProjectSettingsModal setIsOpenEditModal={setIsOpenEditModal} />}
      {isOpenWorkflowModal && !tableSettings.isDirectory && (
        <ProjectWorkflowModal setIsOpenWorkflowModal={setIsOpenWorkflowModal} />
      )}
      {isOpenBusinessRulesModal && (
        <ProjectBusinessRulesModal setIsOpenBusinessRulesModal={setIsOpenBusinessRulesModal} />
      )}
      {isOpenTotalsRowModal && (
        <ProjectTotalsRowModal setIsOpenTotalsRowModal={setIsOpenTotalsRowModal} />
      )}
      {isOpenConsolidationModal && (
        <ProjectConsolidationModal setIsOpenConsolidationModal={setIsOpenConsolidationModal} />
      )}
      {columnAccessColumnName && (
        <ProjectColumnAccessModal
          tableId={tableSettings.id}
          formLabel={tableSettings.connection || tableSettings.formName}
          initialColumnName={columnAccessColumnName}
          onClose={() => setColumnAccessColumnName(null)}
          onSaved={() => {
            void reloadCurrentTableSettings();
            refreshTable();
          }}
        />
      )}
      {isOpenImportModal && <ProjectImportDataModal onClose={() => setIsOpenImportModal(false)} />}
      {isOpenSubsetModal && (
        <BatchSubsetOperationModal
          tableId={tableSettings.id}
          formStructure={memoTableSettings?.formStructure || []}
          onClose={() => setIsOpenSubsetModal(false)}
          onApplied={() => refreshTable()}
        />
      )}
      {cellMetaModal.isOpen && cellMetaModal.target && (
        <TableCellMetaModal
          onClose={() =>
            setCellMetaModal({
              isOpen: false,
              mode: 'comment',
              target: null,
            })
          }
          tableId={tableSettings.id}
          target={cellMetaModal.target}
          initialMode={cellMetaModal.mode}
        />
      )}
    </div>
  );
};
